import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import {
  UtensilsCrossed,
  Search,
  Plus,
  Pencil,
  Trash2,
  ArrowLeft,
  X,
  Flame,
} from "lucide-react";
import MealPlanTemplateForm from "@/components/meals/MealPlanTemplateForm";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const GOAL_PILLS = [
  { id: "all", label: "All Goals" },
  { id: "bulking", label: "Bulking" },
  { id: "cutting", label: "Cutting" },
  { id: "maintenance", label: "Maintenance" },
  { id: "clean_eating", label: "Clean Eating" },
];

export default function AdminNutritionSection({
  onBack,
  initialFilter = "all",
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(initialFilter);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [deletingPlan, setDeletingPlan] = useState(null);

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ["admin_meal_templates"],
    queryFn: () => base44.entities.MealPlanTemplate.list(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.MealPlanTemplate.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_meal_templates"] });
      setDeletingPlan(null);
    },
  });

  const filteredPlans = useMemo(() => {
    return plans.filter((p) => {
      const q = search.toLowerCase().trim();
      const matchSearch =
        !q ||
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.description && p.description.toLowerCase().includes(q));

      if (!matchSearch) return false;

      const goal = (p.goal || "").toLowerCase();
      if (filter === "all") return true;
      return goal === filter;
    });
  }, [plans, search, filter]);

  return (
    <div className="space-y-4 pb-20">
      {/* Top Bar with Back navigation & Title */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="p-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white transition-all cursor-pointer"
            aria-label="Back to Command Center"
          >
            <ArrowLeft className="w-4 h-4 text-sky-400" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-white leading-tight">Nutrition Plans</h1>
            <p className="text-[11px] text-slate-400">
              {filteredPlans.length} of {plans.length} meal plans
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingPlan(null);
            setShowForm(true);
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-xs shadow-md active:scale-95 transition-all cursor-pointer"
          data-testid="create-meal-plan-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>New Plan</span>
        </button>
      </div>

      {/* Search Input */}
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search meal plans by title…"
          className="w-full pl-9 pr-8 py-2 rounded-xl bg-[#071326] border border-white/10 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-sky-400"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Filter Horizontal Pills */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
        {GOAL_PILLS.map((pill) => {
          const isActive = filter === pill.id;
          return (
            <button
              key={pill.id}
              type="button"
              onClick={() => setFilter(pill.id)}
              className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                isActive
                  ? "bg-sky-500 text-slate-950 shadow-sm"
                  : "bg-white/5 text-slate-400 hover:text-white border border-white/5"
              }`}
            >
              {pill.label}
            </button>
          );
        })}
      </div>

      {/* Meal Plan Cards List */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-2xl bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : filteredPlans.length === 0 ? (
        <div className="rounded-2xl p-8 bg-[#071326] border border-white/10 text-center space-y-2">
          <UtensilsCrossed className="w-8 h-8 text-slate-500 mx-auto" />
          <p className="text-xs font-bold text-white">No meal plans found</p>
          <p className="text-[11px] text-slate-400">
            {search ? "No plans match your search." : "Create your first nutrition template."}
          </p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {filteredPlans.map((plan) => {
            const mealCount = Array.isArray(plan.meals) ? plan.meals.length : 0;

            return (
              <div
                key={plan.id}
                className="p-3.5 rounded-2xl bg-gradient-to-r from-[#08152B] to-[#040C1A] border border-white/10 hover:border-sky-400/40 transition-all shadow-sm"
                data-testid={`meal-plan-card-${plan.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-bold text-white truncate">
                        {plan.name}
                      </span>
                      {plan.goal && (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 text-[9px] font-bold capitalize">
                          {plan.goal.replace("_", " ")}
                        </span>
                      )}
                    </div>

                    {plan.description && (
                      <p className="text-[11px] text-slate-400 line-clamp-1 mb-2">
                        {plan.description}
                      </p>
                    )}

                    <div className="flex items-center gap-3 text-[10px] text-slate-300 flex-wrap">
                      {plan.daily_calories && (
                        <span className="flex items-center gap-1 text-amber-400 font-semibold">
                          <Flame className="w-3 h-3" />
                          {plan.daily_calories} kcal
                        </span>
                      )}
                      {(plan.daily_protein_g || plan.daily_carbs_g || plan.daily_fat_g) && (
                        <span className="text-slate-400">
                          P: {plan.daily_protein_g || 0}g | C: {plan.daily_carbs_g || 0}g | F:{" "}
                          {plan.daily_fat_g || 0}g
                        </span>
                      )}
                      <span className="text-slate-400">
                        {mealCount} meal{mealCount !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        setEditingPlan(plan);
                        setShowForm(true);
                      }}
                      className="p-2 rounded-xl bg-white/5 hover:bg-sky-500/20 text-slate-300 hover:text-sky-300 border border-white/10 active:scale-95 transition-all cursor-pointer"
                      aria-label="Edit Plan"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeletingPlan(plan)}
                      className="p-2 rounded-xl bg-white/5 hover:bg-red-500/20 text-slate-300 hover:text-red-400 border border-white/10 active:scale-95 transition-all cursor-pointer"
                      aria-label="Delete Plan"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Form Dialog */}
      {showForm && (
        <MealPlanTemplateForm
          open={showForm}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setShowForm(false);
              setEditingPlan(null);
            }
          }}
          initialData={editingPlan}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["admin_meal_templates"] });
            setShowForm(false);
            setEditingPlan(null);
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deletingPlan && (
        <AlertDialog open onOpenChange={() => setDeletingPlan(null)}>
          <AlertDialogContent className="bg-[#030A17] border border-white/10 text-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Meal Plan</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-300 text-xs">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-white">{deletingPlan.name}</span>? This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteMutation.mutate(deletingPlan.id)}
                className="bg-red-500 text-white hover:bg-red-600"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
